import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { NavItem } from './NavItem';
import { cn } from '@/utils/cn';

export interface NavGroupChild {
  icon: LucideIcon;
  label: string;
  to: string;
}

interface NavGroupProps {
  icon: LucideIcon;
  label: string;
  children: NavGroupChild[];
  isCollapsed?: boolean;
  onExpand?: () => void;
}

export const NavGroup: React.FC<NavGroupProps> = ({
  icon: Icon,
  label,
  children,
  isCollapsed = false,
  onExpand,
}) => {
  const { pathname } = useLocation();
  const storageKey = `nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const isChildActive = children.some((c) =>
    c.to === '/' ? pathname === '/' : pathname === c.to || pathname.startsWith(c.to + '/')
  );

  const [isOpen, setIsOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return stored === 'true';
    } catch {
      // ignore
    }
    return isChildActive;
  });

  // Auto-expand when navigating to a child route
  useEffect(() => {
    if (isChildActive && !isOpen) {
      setIsOpen(true);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (isCollapsed) {
      onExpand?.();
      return;
    }
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // ignore
    }
  };

  if (isCollapsed) {
    // Collapsed: show group icon as a button with tooltip — clicking expands sidebar
    return (
      <div className="relative group/tip">
        <button
          onClick={toggle}
          title={label}
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200',
            'text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100/80',
            isChildActive && 'bg-primary-50 text-primary-600'
          )}
        >
          <Icon className="w-5 h-5" />
        </button>
        {/* Tooltip */}
        <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-secondary-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
          {label}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Group header */}
      <button
        onClick={toggle}
        className={cn(
          'w-full flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
          'hover:bg-secondary-100/80 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          isChildActive && !isOpen
            ? 'text-primary-600'
            : 'text-secondary-600 hover:text-secondary-900'
        )}
      >
        <Icon
          className={cn(
            'w-5 h-5 mr-3 flex-shrink-0 transition-colors duration-200',
            isChildActive ? 'text-primary-500' : 'text-secondary-400'
          )}
        />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-secondary-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Children */}
      {isOpen && (
        <div className="mt-0.5 ml-3 pl-3 border-l border-secondary-200 space-y-0.5">
          {children.map((child) => (
            <NavItem
              key={child.to}
              icon={child.icon}
              label={child.label}
              to={child.to}
              isCollapsed={false}
              className="py-2 text-sm"
            />
          ))}
        </div>
      )}
    </div>
  );
};
